import React, { useContext, useCallback, useRef } from "react";
import { AuthContext } from "../context/Authentication";
import { useSnackbar } from "../context/SnackbarContext";
import {
  transferGuestCartToAuthenticated,
  retryFailedTransfer,
  clearGuestCartAfterTransfer,
  getProductNamesFromItems,
} from "../services/cartTransferService";
import { getAllGuestCartItems } from "../utils/guestCartDB";

/**
 * Custom hook for handling guest cart transfer on authentication
 * Integrates with Authentication context and Snackbar context
 * Provides automatic transfer on LOGIN action
 */
export const useCartTransfer = () => {
  const { state, dispatch } = useContext(AuthContext);
  const { showSuccess, showError, showWarning, showInfo, hideSnackbar } =
    useSnackbar();
  const transferInProgress = useRef(false);

  /**
   * Get authenticated cart items for a specific user
   * This function accepts a user parameter instead of relying on state
   */
  const getCurrentCartItemsForUser = useCallback(async (user) => {
    try {
      // Check if we have a valid user
      if (!user || !user.User_Id) {
        return [];
      }

      const { postRecord } = await import("../services/services");

      const response = await postRecord("cart/getmycart", {
        User_Id: user.User_Id,
      });

      // Handle response properly
      if (response && response.status === 200 && response.data) {
        if (response.data.Response_Data) {
          const result = JSON.parse(response.data.Response_Data);
          const cartItems = Array.isArray(result) ? result : result.data || [];
          return cartItems;
        } else if (Array.isArray(response.data)) {
          return response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          return response.data.data;
        }
      }

      return [];
    } catch (error) {
      return [];
    }
  }, []);

  /**
   * Get current authenticated user's cart items (backward compatibility)
   * This would typically come from an API call or global state
   */
  const getCurrentCartItems = useCallback(async () => {
    return getCurrentCartItemsForUser(state.user);
  }, [state.user, getCurrentCartItemsForUser]);

  /**
   * Handle the actual cart transfer process
   */
  const performCartTransfer = useCallback(
    async (retryItems = null, userToUse = null) => {
      if (transferInProgress.current) {
        return;
      }

      transferInProgress.current = true;

      try {
        // Use provided user or fallback to state.user
        const currentUser = userToUse || state.user;

        // Check if we have guest cart items to transfer
        const guestCartItems = retryItems || (await getAllGuestCartItems());

        if (!guestCartItems || guestCartItems.length === 0) {
          transferInProgress.current = false;
          return;
        }

        // Get current authenticated cart items using the currentUser (not state.user)
        const currentCartItems = await getCurrentCartItemsForUser(currentUser);

        // Show initial transfer message
        const transferMessage = retryItems
          ? `Retrying ${retryItems.length} failed items...`
          : `Transferring ${guestCartItems.length} items to your cart...`;

        const transferId = showInfo(transferMessage, {
          persistent: true,
        });

        // Perform the transfer
        let result;
        if (retryItems) {
          // Retry failed items
          result = await retryFailedTransfer(
            retryItems,
            currentCartItems,
            currentUser,
            (current, total, itemResult) => {
              // Optional: Update progress message
            }
          );
        } else {
          // Normal transfer
          result = await transferGuestCartToAuthenticated(
            currentCartItems,
            currentUser,
            (current, total, itemResult) => {
              // Optional: Update progress message
            }
          );
        }

        // Hide the transfer progress message first
        hideSnackbar(transferId);

        if (result.success) {
          // Handle successful transfer
          if (result.successfulItems.length > 0) {
            showSuccess(result.message);

            // Always clear guest cart after successful transfer
            if (!retryItems) {
              try {
                const cleared = await clearGuestCartAfterTransfer();
                if (cleared) {
                  // Clear both IndexedDB and context state
                  dispatch({ type: "CLEAR_GUEST_CART" });

                  // Trigger cart refresh in AddToCart component
                  dispatch({ type: "REFRESH_CART" });
                }
              } catch (clearError) {
                showError(
                  "Transfer completed but failed to clear guest cart. Please refresh the page."
                );
              }
            }
          } else if (result.skippedItems && result.skippedItems.length > 0) {
            showInfo(result.message);

            // Clear guest cart since items were processed (skipped means they were already in cart)
            if (!retryItems) {
              try {
                const cleared = await clearGuestCartAfterTransfer();
                if (cleared) {
                  dispatch({ type: "CLEAR_GUEST_CART" });

                  // Trigger cart refresh in AddToCart component
                  dispatch({ type: "REFRESH_CART" });
                }
              } catch (clearError) {
                // Silent failure for skipped transfer clearing
              }
            }
          }

          // Handle partial failures
          if (result.failedItems.length > 0) {
            const productNames = getProductNamesFromItems(result.failedItems);
            const failureMessage = `Failed to transfer: ${productNames.join(
              ", "
            )}`;

            showError(failureMessage, {
              persistent: true,
              action: (
                <button
                  onClick={() => handleRetryTransfer(result.failedItems)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "inherit",
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontSize: "0.875rem",
                  }}
                >
                  Retry
                </button>
              ),
            });
          }
        } else {
          // Complete failure
          const productNames = getProductNamesFromItems(
            result.failedItems || guestCartItems
          );
          const errorMessage = `Cart transfer failed. Items: ${productNames.join(
            ", "
          )}`;

          showError(errorMessage, {
            persistent: true,
            action: (
              <button
                onClick={() =>
                  handleRetryTransfer(result.failedItems || guestCartItems)
                }
                style={{
                  background: "none",
                  border: "none",
                  color: "inherit",
                  textDecoration: "underline",
                  cursor: "pointer",
                  fontSize: "0.875rem",
                }}
              >
                Retry
              </button>
            ),
          });
        }
      } catch (error) {
        showError("Failed to transfer cart items. Please try again.");
      } finally {
        transferInProgress.current = false;
      }
    },
    [showSuccess, showError, showWarning, showInfo, hideSnackbar, dispatch]
  );

  /**
   * Handle retry of failed items
   */
  const handleRetryTransfer = useCallback(
    async (failedItems) => {
      if (!failedItems || failedItems.length === 0) {
        return;
      }

      // Check if user is still authenticated
      if (!state.isAuthenticated || !state.user || !state.user.User_Id) {
        showError("Please log in again to retry cart transfer.");
        return;
      }

      try {
        const currentCartItems = await getCurrentCartItemsForUser(state.user);

        showInfo(`Retrying ${failedItems.length} failed items...`, {
          autoHideDuration: 2000,
        });
        const retryResult = await retryFailedTransfer(
          failedItems,
          currentCartItems,
          state.user
        );

        if (retryResult.success && retryResult.successfulItems.length > 0) {
          showSuccess(retryResult.message);
        }

        // If still have failures after retry, ask user to clear them
        if (retryResult.failedItems && retryResult.failedItems.length > 0) {
          const productNames = getProductNamesFromItems(
            retryResult.failedItems
          );
          const finalErrorMessage = `Still failed: ${productNames.join(
            ", "
          )}. Add these items manually?`;

          showWarning(finalErrorMessage, {
            persistent: true,
            action: (
              <button
                onClick={() => handleClearFailedItems(retryResult.failedItems)}
                style={{
                  background: "none",
                  border: "none",
                  color: "inherit",
                  textDecoration: "underline",
                  cursor: "pointer",
                  fontSize: "0.875rem",
                }}
              >
                Clear Items
              </button>
            ),
          });
        }
      } catch (error) {
        showError("Retry failed. Please try again or clear the items.");
      }
    },
    [showSuccess, showError, showWarning, showInfo]
  );

  /**
   * Handle clearing of failed items after retry attempts
   */
  const handleClearFailedItems = useCallback(
    async (failedItems) => {
      try {
        const productNames = getProductNamesFromItems(failedItems);

        // Clear the guest cart
        const cleared = await clearGuestCartAfterTransfer();
        if (cleared) {
          dispatch({ type: "CLEAR_GUEST_CART" });
          showInfo(`Cleared failed items: ${productNames.join(", ")}`);
        } else {
          showError("Failed to clear items. Please refresh the page.");
        }
      } catch (error) {
        showError("Failed to clear items. Please refresh the page.");
      }
    },
    [showInfo, showError, dispatch]
  );

  /**
   * Manual trigger for cart transfer
   * Can be called from components when needed
   */
  const triggerCartTransfer = useCallback(async () => {
    if (!state.isAuthenticated) {
      return;
    }

    if (!state.user?.User_Id) {
      return;
    }

    // Check if there are guest cart items BEFORE showing transfer message
    const guestItems = await getAllGuestCartItems();
    if (!guestItems || guestItems.length === 0) {
      return;
    }

    // Execute transfer immediately
    await performCartTransfer(null, state.user);
  }, [state.isAuthenticated, state.user, performCartTransfer]);

  /**
   * Check if cart transfer should be triggered on authentication
   * This should be called after LOGIN action is dispatched
   */
  const checkAndTriggerTransfer = useCallback(async () => {
    if (!state.isAuthenticated || transferInProgress.current) {
      return;
    }

    if (!state.user || !state.user.User_Id) {
      return;
    }

    // Capture the user object to prevent closure issues
    const currentUser = state.user;

    // Execute transfer immediately - no delay needed
    await performCartTransfer(null, currentUser);
  }, [state.isAuthenticated, state.user, performCartTransfer]);

  // Reset transfer state when user logs out
  React.useEffect(() => {
    if (!state.isAuthenticated) {
      transferInProgress.current = false;
    }
  }, [state.isAuthenticated]);

  return {
    triggerCartTransfer,
    checkAndTriggerTransfer,
    isTransferInProgress: transferInProgress.current,
  };
};

export default useCartTransfer;
